/**
 * /security - the signed-in operator's second factor (OCP-755).
 *
 * Not set up: start -> QR code + the secret for manual entry -> the first
 * code switches it on -> the backup codes are shown ONCE. Set up: status,
 * new backup codes (with a current code), and - for roles where it is not
 * required - switching it off. A super_admin without a factor lands here
 * from every other page (ProtectedRoute) until it is done.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { fmtDateTime } from '../lib/format-time';

interface MfaStatus {
  enabled: boolean;
  enabled_at: string | null;
  backup_codes_left: number;
  required: boolean;
}

interface Enrolment {
  secret: string;
  otpauth_url: string;
  qr_data_uri: string;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string; message?: string } | null;
    if (b?.error === 'mfa_invalid_code') return 'Wrong code. Check the time on your device and use the current code.';
    return b?.message ?? `Request failed (HTTP ${e.status}).`;
  }
  return 'Request failed.';
}

export function SecurityPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [enrol, setEnrol] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setStatus(await api<MfaStatus>('/v1/admin/me/mfa'));
    } catch (e) {
      setErr(errorText(e));
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function run<T>(fn: () => Promise<T>): Promise<T | null> {
    if (busy) return null;
    setBusy(true);
    setErr(null);
    try {
      return await fn();
    } catch (e) {
      setErr(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  const start = () => run(async () => setEnrol(await api<Enrolment>('/v1/admin/me/mfa/start', { method: 'POST', body: {} })));
  const confirm = () =>
    run(async () => {
      const res = await api<{ backup_codes: string[] }>('/v1/admin/me/mfa/confirm', { method: 'POST', body: { totp_code: code.trim() } });
      setBackupCodes(res.backup_codes);
      setEnrol(null);
      setCode('');
      await load();
      await refresh();
    });
  const regenerate = () =>
    run(async () => {
      const res = await api<{ backup_codes: string[] }>('/v1/admin/me/mfa/backup-codes', { method: 'POST', body: { totp_code: code.trim() } });
      setBackupCodes(res.backup_codes);
      setCode('');
      await load();
    });
  const disable = () =>
    run(async () => {
      await api('/v1/admin/me/mfa/disable', { method: 'POST', body: { totp_code: code.trim() } });
      setCode('');
      await load();
      await refresh();
    });

  const codeInput = (
    <input
      value={code}
      onChange={(e) => setCode(e.target.value)}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="6-digit code"
      className="w-36 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-2 py-1.5 text-sm tracking-widest"
      data-testid="mfa-code"
    />
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Security</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Second factor for {user?.email}: an authenticator app (TOTP) plus one-time backup codes.</p>
      </div>

      {user?.mfa_enrollment_required && (
        <div className="rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300" data-testid="mfa-required-banner">
          Your role (super_admin) needs a second factor. Set it up here - every other page opens once it is active.
        </div>
      )}
      {err && <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{err}</div>}

      {backupCodes && (
        <div className="space-y-2 rounded border border-gray-200 dark:border-gray-800 p-4" data-testid="mfa-backup-codes">
          <p className="text-sm font-medium">Backup codes - shown once</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Each code signs you in once when the authenticator is not at hand. Store them in your password manager now.</p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button type="button" onClick={() => { setBackupCodes(null); if (status?.enabled) navigate(from, { replace: true }); }} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700">
            I stored them
          </button>
        </div>
      )}

      {status && !status.enabled && !backupCodes && (
        <div className="space-y-3 rounded border border-gray-200 dark:border-gray-800 p-4">
          {!enrol ? (
            <>
              <p className="text-sm">No second factor yet.</p>
              <button type="button" disabled={busy} onClick={() => void start()} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-50" data-testid="mfa-start">
                Set up an authenticator
              </button>
            </>
          ) : (
            <>
              <p className="text-sm">Scan the code with your authenticator app (1Password, Bitwarden, Google Authenticator, ...), then enter the code it shows.</p>
              <p className="text-xs text-gray-600 dark:text-gray-400" data-testid="mfa-signout-note">Turning it on signs this account out everywhere else: other browser sessions and connected AI clients (MCP) sign in again with the new factor. This session stays signed in.</p>
              <img src={enrol.qr_data_uri} alt="Authenticator QR code" className="h-48 w-48 rounded border border-gray-200 bg-white p-1" />
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Or enter this key by hand: <span className="select-all font-mono" data-testid="mfa-secret">{enrol.secret}</span>
              </p>
              <div className="flex items-center gap-2">
                {codeInput}
                <button type="button" disabled={busy || code.trim().length < 6} onClick={() => void confirm()} className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-50" data-testid="mfa-confirm">
                  Turn on
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {status?.enabled && !backupCodes && (
        <div className="space-y-3 rounded border border-gray-200 dark:border-gray-800 p-4" data-testid="mfa-enabled">
          <p className="text-sm text-green-700 dark:text-green-400">
            Active since {status.enabled_at ? fmtDateTime(status.enabled_at) : '-'} · {status.backup_codes_left} backup code{status.backup_codes_left === 1 ? '' : 's'} left
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {codeInput}
            <button type="button" disabled={busy || code.trim().length < 6} onClick={() => void regenerate()} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm disabled:opacity-50">
              New backup codes
            </button>
            {!status.required && (
              <button type="button" disabled={busy || code.trim().length < 6} onClick={() => void disable()} className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50">
                Turn off
              </button>
            )}
          </div>
          {status.required && <p className="text-xs text-gray-500">Required for your role. A lost device is reset by another super_admin (Team page).</p>}
        </div>
      )}
    </div>
  );
}
